<template>
  <div class="accountants-signup-form">
    <h2 class="text-campton-bold text-accountants">{{ $gettext('Signup as an accountant') }}</h2>
    <b-form @submit.prevent="submitSignup" class="gk-clear-form">
      
      <b-form-group id="fieldset-signup-companyname" :label="$gettext('Company Name*')" label-for="input-signup-companyname" :class="{ 'accepted': (!loadingStepTuah && signupStep === 2) }">
        <div class="position-relative">
          <b-form-input 
            type="text"
            id="input-signup-companyname"
            :placeholder="$gettext('Example Inc.')"
            v-model="companyname"
            trim required
            :disabled="(!loadingStepTuah && signupStep === 2)"
          />
          <div class="input-check">
            <font-awesome-icon icon="fa-solid fa-check" />
          </div>
        </div>
      </b-form-group>

      <b-form-group id="fieldset-signup-username" :label="$gettext('Your full name*')" label-for="input-signup-username" :class="{ 'accepted': (!loadingStepTuah && signupStep === 2) }">
        <div class="position-relative">
          <b-form-input 
            type="text"
            id="input-signup-username"
            :placeholder="randomPlaceholderName"
            v-model="username"
            trim required
            :disabled="(!loadingStepTuah && signupStep === 2)"
          />
          <div class="input-check">
            <font-awesome-icon icon="fa-solid fa-check" />
          </div>
        </div>
      </b-form-group>

      <b-form-group id="fieldset-signup-email" :label="$gettext('Email address*')" label-for="input-signup-email" :class="{ 'accepted': (!loadingStepTuah && signupStep === 2) }">
        <div class="position-relative">
          <b-form-input 
            type="email"
            id="input-signup-email"
            :placeholder="randomPlaceholderEmail"
            v-model="email"
            trim required
            :disabled="(!loadingStepTuah && signupStep === 2)"
          />
          <div class="input-check">
            <font-awesome-icon icon="fa-solid fa-check" />
          </div>
        </div>
      </b-form-group>

      <b-form-group id="fieldset-signup-phone" :label="$gettext('Phone number*')" label-for="input-signup-phone" :class="{ 'accepted': (!loadingStepTuah && signupStep === 2) }">
        <div class="position-relative">
          <b-form-input 
            type="text"
            id="input-signup-phone"
            v-model="phone"
            trim required
            :disabled="(!loadingStepTuah && signupStep === 2)"
          />
          <div class="input-check">
            <font-awesome-icon icon="fa-solid fa-check" />
          </div>
        </div>
      </b-form-group>

      <p v-if="showEmailWarning" class="text-danger text-center">
        <font-awesome-icon icon="fa-regular fa-warning" />
        {{ emailWarningTxt }}
      </p>

      <div class="step-tuah" :class="{ expanded: !loadingStepTuah && (signupStep === 2) }" style="padding-top: 1rem;">
        <h3>{{ $gettext('Branding options') }}</h3>

        <b-form-group label-cols="6" label-size="sm" :label="$gettext('Company color*')" label-for="input-sm">
          <b-form-input type="color" class="input-color" size="sm" v-model="companycolor" />
          <span class="picker-hex" :style="{ color: getHighContrastColor(companycolor) }">{{ companycolor }}</span>
        </b-form-group>

        <b-form-group id="fieldset-companylogo" :label="$gettext('Company logo*')" label-for="input-companylogo">
          <VueDropzone :allowed-files="'all'" :onlyChange="true" @change="fileChanged" />
        </b-form-group>
        
        <p v-if="showLogoWarning" class="text-danger text-center">
          <font-awesome-icon icon="fa-regular fa-warning" />
          {{ $gettext('Please upload your company logo.') }}
        </p>

        <div class="topbar-preview-wrap">
          <small class="form-text">{{ $gettext('Your customers will see the top bar in Gekko as shown below') }}</small>
          <div class="topbar-preview" :style="'background-color: ' + companycolor + ';'">
            <div class="topbar-preview__avatar">
              <img v-if="companylogo && logoURL" :src="logoURL" alt="">
            </div>
            <div class="topbar-preview__company">
              <div class="topbar-preview__company--name">{{ $gettext('Customer\'s company') }}</div>
              <div class="topbar-preview__company--customer">{{ $gettext('Customer\'s name') }}</div>
            </div>
            <div class="topbar-preview__tabs">
              <div class="topbar-preview__tabs--tab active">
                <font-awesome-icon icon="fa-solid fa-house" />
                {{ $gettext('Dashboard') }}
              </div>
              <!-- <div class="topbar-preview__tabs--tab">
                <font-awesome-icon icon="fa-solid fa-file-lines" />
                {{ $gettext('Invoices') }}
              </div> -->
              <!-- <div class="topbar-preview__tabs--tab">
                <font-awesome-icon icon="fa-solid fa-cart-shopping" />
                {{ $gettext('Costs') }}
              </div> -->
              <!-- <div class="topbar-preview__tabs--tab">
                <font-awesome-icon icon="fa-regular fa-clock" />
                {{ $gettext('Hours') }}
              </div> -->
              <!-- <div class="topbar-preview__tabs--tab">
                <font-awesome-icon icon="fa-solid fa-car" />
                {{ $gettext('Trips') }}
              </div> -->
            </div>
          </div>
        </div>

        <b-form-group id="fieldset-signup-language" :label="$gettext('Preferred language*')" label-for="input-signup-language">
          <b-form-select
            id="input-signup-language" 
            v-model="preferredlanguage" 
            :options="languageOptions" 
            :required="!loadingStepTuah && (signupStep === 2)"
            :disabled="signupStep === 1"
          />
        </b-form-group>

        <b-form-group id="fieldset-signup-password" :label="$gettext('Password*')" label-for="input-signup-password">
          <div class="position-relative">
            <b-form-input
              type="password"
              id="input-signup-password"
              v-model="password"
              trim
              :required="!loadingStepTuah && (signupStep === 2)"
              :disabled="signupStep === 1"
              @focus="showPasswordHint = true"
              @blur="() => { showPasswordHint = (password.length > 0) }"
            />
            <ShowPasswordIcon />
          </div>
          <PasswordStrengthHint :show="showPasswordHint" :password="password" @score-changed="(score) => { passwordScore = score }" />
        </b-form-group>
      </div>

      <div style="padding-top: 0.5rem; width: 100%;">
        <GekkoButton
          @click="signupStep = 1"
          :text="$gettext('Previous step')"
          variant="link"
          :disabled="signupStep === 1"
          :style="'opacity: ' + ((signupStep === 1) ? 0 : 1) + ' !important;'"
          class=""
        />
        <GekkoButton
          type="submit"
          :text="(signupStep === 1) ? $gettext('Get started') : $gettext('And you are done!')"
          :loading="loadingStepTuah || savingData"
          :disabled="(signupStep === 2 && (passwordScore < 1))"
          variant="accountants"
          class="w-100 text-campton-book position-relative"
          size="xl"
        />
      </div>
    </b-form>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"

import { randomCelebName, randomEmail, getHighContrastColor } from "@/utils/"

import GekkoButton from "@/components/GekkoButton"
import ShowPasswordIcon from "@/components/ui/ShowPasswordIcon"
import PasswordStrengthHint from "@/components/ui/PasswordStrengthHint"
import VueDropzone from "@/components/VueDropzone";

export default {
  name: 'SignupFormAccountants',
  components: {
    GekkoButton,
    ShowPasswordIcon,
    PasswordStrengthHint,
    VueDropzone
  },
  data () {
    return {
      companyname: '',
      username: '',
      email: '',
      phone: '',
      companycolor: '#22AC8A',
      companylogo: null,
      preferredlanguage: '',
      password: '',
      showPasswordHint: false,

      showEmailWarning: false,
      emailWarningTxt: '',

      showLogoWarning: false,
      
      passwordScore: 0,
      signupStep: 1,
      loading: false,
      loadingUserLocation: false,
      savingData: false
    }
  },
  computed: {
    ...mapGetters({
      languageOptions: 'signup/getLanguageOptions',
      userLanguage: 'signup/getUserLanguage',
    }),
    randomPlaceholderName () {
      return randomCelebName()
    },
    randomPlaceholderEmail () {
      return randomEmail(this.randomPlaceholderName)
    },
    loadingStepTuah () {
      return this.loading || this.loadingLanguages || this.loadingUserLocation
    },
    logoURL () {
      let logo_url = null
      if (this.companylogo) {
        logo_url = URL.createObjectURL(this.companylogo)
      }
      return logo_url
    }
  },
  created () {
  },
  mounted () {
  },
  methods: {
    getHighContrastColor,
    ...mapActions({ 
      saveData: 'signup/signUpAccountants',
      checkAvailability: 'signup/checkAvailability',
      geoLocateUserIp: 'signup/geoLocateUserIp',
      getAccessToken: "auth/getAccessToken"
    }),
    submitSignup () {
      if (this.signupStep === 1) {
        this.checkEmailAvailability()
      }
      if (this.signupStep === 2 && this.checkFormValidity()) {
        const data = {
          company_name: this.companyname,
          full_name: this.username,
          email: this.email,
          password: this.password,
          preferred_language: this.preferredlanguage,
          phone_number: this.phone,
          logo: this.companylogo,
          color: this.companycolor
        }
        this.savingData = true
        this.saveData(data).then(() => {
          console.log('saved data, trying login')
          this.login()
        }).catch((error) => {
          console.log('error', error)
        }).finally(() => {
          this.savingData = false
        })
      }
    },
    checkFormValidity () {
      let is_valid = true
      if (this.passwordScore < 1) {
        is_valid = false
      }
      if (!this.companylogo) {
        is_valid = false
        this.showLogoWarning = true
      }
      return is_valid
    },
    fileChanged (file) {
      if (file) {
        this.showLogoWarning = false
      }
      this.companylogo = file
    },
    checkEmailAvailability () {
      this.loading = true
      this.checkAvailability(this.email).then((response) => {
        this.showEmailWarning = false
        console.log('response', response)
        if (response.status === 200) {
          this.signupStep = 2
          // geo locate user
          this.loadingUserLocation = true
          this.geoLocateUserIp().then(() => {
            this.preferredlanguage = this.userLanguage
            this.loadingUserLocation = false
          })
        }
      }).catch((error) => {
        if (error.status === 409) {
          this.showEmailWarning = true
          this.emailWarningTxt = this.$gettext('This email address already exists')
        } else if (error.status === 422) {
          this.showEmailWarning = true
          this.emailWarningTxt = this.$gettext('That email doesn\'t seem to be ok')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    login () {
      this.loading = true
      this.getAccessToken({
        'username': this.email,
        'password': this.password,
      }).then((response) => {
        console.log('Login.vue::login() -> getAccessToken THEN response', response)
        this.$emit('authorised', false)
      }).catch((error) => {
        console.log('error authenticating new user', error)
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style lang="scss">
  .accountants-signup-form {
    #dropzonePreview {
      height: 175px;
    }
  }
</style>

<style lang="scss" scoped>
  .step-tuah {
    max-height: 0;
    opacity: 0;
    transition: 0.7s ease;
    overflow: hidden;
    &.expanded {
      opacity: 1;
      max-height: 700px;
    }
  }
  .input-color {
    height: 37px !important;
  }
  .picker-hex {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 14px;
    pointer-events: none;
  }
  .topbar-preview-wrap {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
    padding: 1rem 0;
    margin-bottom: 1rem;
    .topbar-preview {
      display: flex;
      gap: 0.5rem;
      align-items: center;
      width: 100%;
      height: 50px;
      padding: 0.25rem 0.5rem;
      color: white;
      &__avatar {
        border-radius: 50em;
        overflow: hidden;
        width: 40px;
        height: 40px;
        background: white;
        position: relative;
        flex-shrink: 0;
        img {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 100%;
          object-fit: cover;
        }
      }
      &__company {
        display: flex;
        flex-direction: column;
        justify-content: center;
        height: 100%;
        color: white;
        font-family: DINNextLTPro-Regular;
        &--name {
          font-size: 26px;
          white-space: nowrap;
          line-height: 1;
          display: block;
          font-family: DINNextLTPro-Medium;
        }
        &--customer {
          font-size: 12px;
          opacity: 0.5;
        }
      }
      &__tabs {
        display: flex;
        gap: 1rem;
        padding-top: 0.25rem;
        flex: 1;
        justify-content: flex-end;
        align-items: center;
        &--tab {
          display: flex;
          flex-direction: column;
          font-size: 12px;
          opacity: 0.7;
          text-align: center;
          .svg-inline--fa { font-size: 20px; }
          &.active {
            opacity: 1;
          }
        }
      }
    }
  }
</style>